import { useQuery } from '@apollo/client'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import MuiLink from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import {
    DataGridPro,
    type GridColDef,
    type GridPaginationModel,
} from '@mui/x-data-grid-pro'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { RecruiterMe } from 'recruiter/graphql/admin.gql'
import { GetCandidateBucketAuditSearch } from 'recruiter/graphql/candidateBucketAudit.gql'
import { recruiterRouteConfig } from 'recruiter/routes'
import { AdminHeader } from './AdminHeader'

const PAGE_SIZE = 50

const columns: GridColDef[] = [
    {
        field: 'candidate',
        headerName: 'Candidate',
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
            <Link
                to={`${recruiterRouteConfig.candidates.path}/${params.row.candidateId}/communications`}
                style={{ color: 'inherit' }}
            >
                {params.row.candidateLastName}, {params.row.candidateFirstName}
            </Link>
        ),
        valueGetter: (_value, row) =>
            `${row.candidateLastName}, ${row.candidateFirstName}`,
        sortable: false,
    },
    {
        field: 'bucket',
        headerName: 'Bucket',
        width: 120,
        sortable: false,
        renderCell: (params) =>
            params.value ? <Chip label={params.value} size="small" /> : '—',
    },
    {
        field: 'reason',
        headerName: 'Bucket Reason',
        flex: 1,
        minWidth: 200,
        sortable: false,
    },
    {
        field: 'resolvedCalendlyLink',
        headerName: 'Calendly Link',
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: (params) =>
            params.value ? (
                <MuiLink href={params.value} target="_blank" rel="noopener">
                    {params.value}
                </MuiLink>
            ) : (
                'None'
            ),
    },
    {
        field: 'linkResolutionReason',
        headerName: 'Link Reason',
        width: 200,
        sortable: false,
    },
    {
        field: 'createdAt',
        headerName: 'Date',
        width: 120,
        sortable: false,
        valueFormatter: (value) => new Date(value).toLocaleDateString(),
    },
]

export default function BucketAuditDashboard() {
    const { data: meData } = useQuery(RecruiterMe)
    const me = meData?.recruiterMe
    const [searchText, setSearchText] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
        {
            page: 0,
            pageSize: PAGE_SIZE,
        }
    )

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(searchText)
            setPaginationModel((prev) => ({ ...prev, page: 0 }))
        }, 300)
        return () => clearTimeout(timeout)
    }, [searchText])

    const { data, loading } = useQuery(GetCandidateBucketAuditSearch, {
        variables: {
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            quickSearch: debouncedSearch || null,
        },
    })

    const rows = data?.candidateBucketAuditSearch?.rows ?? []
    const totalCount = data?.candidateBucketAuditSearch?.totalCount ?? 0

    return (
        <Stack spacing={3} p={3}>
            <AdminHeader
                isAdmin={me?.isAdmin ?? false}
                isBotAdmin={me?.isBotAdmin ?? false}
            />
            <TextField
                placeholder="Search candidates..."
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{ maxWidth: 400 }}
            />
            <Box sx={{ height: 600, bgcolor: 'background.paper' }}>
                <DataGridPro
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    density="compact"
                    getRowId={(row) => row.candidateId}
                    paginationMode="server"
                    rowCount={totalCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[PAGE_SIZE]}
                    rowHeight={41}
                    columnHeaderHeight={56}
                    disableColumnMenu
                    disableColumnReorder
                    disableRowSelectionOnClick
                    sx={{
                        flex: 1,
                        border: 0,
                        '& .MuiDataGrid-cell': {
                            fontSize: '0.8125rem',
                            borderTop: 'none',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                        },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within':
                            {
                                outline: 'none',
                            },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
                            {
                                outline: 'none',
                            },
                        '& .MuiDataGrid-filler > div': {
                            borderTop: 'none',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            pl: 2,
                        },
                    }}
                />
            </Box>
        </Stack>
    )
}
